import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  LinearProgress,
  Fade,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  Event,
  PersonOutline,
  Business,
  LocalHospital,
  MedicalInformation,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getStatistics, getMonthlySummary, Statistics, MonthlySummary } from '../api/api';
// import DebugPanel from './DebugPanel';


const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  subtitle?: string;
}> = ({ title, value, icon, color, trend, subtitle }) => (
  <Card 
    sx={{ 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      border: '1px solid #e2e8f0',
      borderRadius: 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
        borderColor: color,
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography 
            color="text.secondary" 
            variant="body2" 
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}
          >
            {title}
          </Typography>
          <Box 
            sx={{ 
              color: 'white',
              backgroundColor: color,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography 
          variant="h3" 
          component="div" 
          sx={{ 
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Chip 
            label={trend} 
            size="small" 
            sx={{ 
              backgroundColor: '#e8f5e8',
              color: '#2e7d32',
              fontSize: 12,
              fontWeight: 600,
              alignSelf: 'flex-start'
            }} 
          />
        )}
      </Stack>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stats, monthly] = await Promise.all([
          getStatistics(),
          getMonthlySummary(),
        ]);
        setStatistics(stats);
        setMonthlySummary(monthly);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data: ' + (error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h6" align="center" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary">
          Check the network tab in developer tools for detailed error information.
        </Typography>
      </Box>
    );
  }

  if (!statistics || !monthlySummary) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h6" align="center">
          No data available
        </Typography>
      </Box>
    );
  }


  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthlyData = monthlySummary.months.map((data) => ({
    ...data,
    month: monthNames[data.month - 1],
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Medical Records Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Comprehensive overview of your medical data
          </Typography>
        </Box>

        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={3} 
          sx={{ mb: 4 }}
        >
          <StatCard
            title="Total Records"
            value={statistics.total_records.toLocaleString()}
            icon={<Event />}
            color="#6366f1"
            trend="+12% from last month"
            subtitle="All medical records"
          />
          <StatCard
            title="Total Amount"
            value={formatCurrency(statistics.total_amount)}
            icon={<AttachMoney />}
            color="#10b981"
            trend="+8% revenue growth"
            subtitle="Total processed payments"
          />
          <StatCard
            title="Unique Patients"
            value={statistics.unique_patients.toLocaleString()}
            icon={<PersonOutline />}
            color="#f59e0b"
            subtitle="Active patient base"
          />
          <StatCard
            title="Healthcare Partners"
            value={statistics.unique_hospitals}
            icon={<Business />}
            color="#ef4444"
            subtitle="Hospitals & clinics"
          />
        </Stack>

        <Stack spacing={4}>
          <Paper 
            sx={{ 
              p: 4,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1
                }}
              >
                Monthly Activity Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track appointments and payments over time
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="appointments" 
                  fill="#6366f1" 
                  name="Appointments"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="payments" 
                  fill="#10b981" 
                  name="Payments"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Paper 
            sx={{ 
              p: 4,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1
                }}
              >
                Financial Performance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly revenue and payment trends
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis 
                  tickFormatter={(value) => formatCurrency(value).replace('R$', '')}
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="total_amount" 
                  fill="#10b981" 
                  name="Total Amount (R$)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Stack>
        
        {/* <DebugPanel /> */}
      </Box>
    </Fade>
  );
};

export default Dashboard;